import './defines';
import ButtonToggleComponent from 'components/button-toggle';
import type { GroupComponentListener } from 'interfaces/app';
import mitt from 'mitt';
import { vueUtils } from 'utils';
import {
	Model,
	Public,
} from 'utils/decorators';
import {
	Component,
	toNative,
} from 'utils/vue-facing-decorator';
import {
	Provide,
	Vue,
	type VueExtended,
	Watch,
} from 'vue-facing-decorator';
import Template from './template.vue';

@Component({
	name: 'ButtonToggleGroupComponent',
	mixins: [Template],
})
class ButtonToggleGroupComponent extends (Vue as typeof VueExtended) {
	@Model({
		default: undefined,
		description: 'Defines the value of the button toggle group (value of the selected button toggle)',
		type: [
			Number,
			String,
		],
	})
	public readonly value?: number | string;

	@Provide
	protected emitter = mitt<GroupComponentListener<InstanceType<typeof ButtonToggleComponent>['value']>>();

	private internalValue: number | string | null = null;

	private resizeObserver?: ResizeObserver;

	protected beforeUnmount(): void {
		this.resizeObserver?.disconnect();
		this.resizeObserver = undefined;
	}

	protected created(): void {
		this.emitter.on(
			'update:checked',
			this.onButtonToggleChange.bind(this),
		);
	}

	protected mounted(): void {
		const rootElement = this.$el as HTMLDivElement;
		const parentElement = (
			rootElement.closest('.horizontal-arrow-scroll-component-container')
			|| rootElement.parentElement
		);

		if (parentElement) {
			this.resizeObserver = new ResizeObserver(this.checkChildrenScroll);
			this.resizeObserver.observe(parentElement);
		}
	}

	protected updated(): void {
		/**
		 * Update could be triggered by a child component,
		 * in that case we need to perform a check on the
		 * internal value to make sure it is in sync with
		 * the checked state of the child components.
		 */
		this.$nextTick(this.onInternalValueChange);
	}

	@Watch('internalValue')
	protected onInternalValueChange(): void {
		this.childrenSyncValues();

		if (this.internalValue !== this.value) {
			requestAnimationFrame(this.checkChildrenScroll);
		}
	}

	@Watch(
		'value',
		{
			immediate: true,
		},
	)
	protected onValueChange(): void {
		this.internalValue = (
			this.value
			?? null
		);
		requestAnimationFrame(this.checkChildrenScroll);
	}

	@Public()
	public checkChildrenScroll(): Decorated<void> {
		const selectedChild = this.childrenSyncValues();
		const rootElement = this.$el as HTMLDivElement;

		if (
			selectedChild?.$el
			&& rootElement.parentElement
		) {
			const parentElement = (
				rootElement.closest('.horizontal-arrow-scroll-component-container')
				|| rootElement.parentElement
			);
			const parentElementComputedStyle = getComputedStyle(parentElement);

			if (
				parentElementComputedStyle.overflowX
				|| parentElementComputedStyle.overflowY
			) {
				const selectedChildElement = selectedChild.$el as HTMLElement;
				selectedChildElement.scrollIntoView({
					behavior: 'smooth',
				});
			}
		}
	}

	@Public()
	public childrenSyncValues(): Decorated<InstanceType<typeof ButtonToggleComponent>> | undefined {
		/**
		* A manual update could be triggered by a child component,
		* in that case we need to perform a check on the
		* internal value to make sure it is in sync with
		* the checked state of the child components.
		*/
		const children = this.$children || [];
		let selectedChild: InstanceType<typeof ButtonToggleComponent> | undefined;

		for (const child of children) {
			if (child.$.type.__vfdConstructor === ButtonToggleComponent.__vfdConstructor) {
				const typedChild = child as InstanceType<typeof ButtonToggleComponent>;

				if (typedChild.value === this.internalValue) {
					selectedChild = typedChild;
					vueUtils.setComponentProp(
						typedChild,
						'checked',
						true,
					);
				} else {
					vueUtils.setComponentProp(
						typedChild,
						'checked',
						false,
					);
				}
			}
		}

		return selectedChild as Decorated<InstanceType<typeof ButtonToggleComponent>> | undefined;
	}

	private emitChange(): void {
		if (this.internalValue !== this.value) {
			this.$emit(
				'update:value',
				this.internalValue,
			);
			requestAnimationFrame(this.checkChildrenScroll);
		}
	}

	private onButtonToggleChange([
		checked,
		value,
	]: [boolean, InstanceType<typeof ButtonToggleComponent>['value'],
	]): void {
		if (checked) {
			this.internalValue = (
				value
				?? null
			);
			this.emitChange();
		}
	}
}

export default toNative(ButtonToggleGroupComponent);
